/**
 * Mailbox: local types, mock seed data and simulated round-trip timings.
 *
 * TEMPORARY: there is no provider backend yet, so the section runs entirely on this seed plus the
 * local reducer (see the page folder's `state.ts`). When the real endpoints land, DELETE this file
 * and derive the same shapes from per-brand selectors. Only DATA lives here (mailboxes, quotas,
 * entitlement, dates); all UI copy lives in the components.
 *
 * The types are declared HERE rather than in the shared area types file on purpose: this section's
 * shapes would pollute the dozen unrelated pages that consume the shared file. Keeping them local
 * also let the two sibling mail sections that were built alongside this one be deleted whole without
 * touching shared types (they since were).
 *
 * Like the other sections, the seed is keyed to the active brand by list position (cycling), so
 * switching brands walks the section through every state it can show without clearing storage.
 */
/**
 * A mailbox's status AT THE PROVIDER, as `GET /domains/{domain}/accounts` returns it. Three
 * members, and nothing in between: creation is SYNCHRONOUS (a successful create means the mailbox
 * exists and can sign in and send at once), so there is no per-mailbox `provisioning` or
 * `pending_dns`, and no `awaiting_payment` (the flow is pay, then create; a row exists only once the
 * create returned). The asynchronous part is DNS, and it belongs to the DOMAIN, not the mailbox
 * (see {@link DomainStatus}); a provisioning failure is likewise a domain `failed`, not a mailbox one.
 *
 * `suspended` and `deleted` are set by the provider; this section can neither cause nor clear them
 * (there is no suspend/resume or restore in the service). `deleted` rows can be RETURNED by the list
 * endpoint, so the UI must render them: no primary action, a status pill, and no webmail.
 */
export type MailboxStatus = 'active' | 'suspended' | 'deleted';

/** Who a mailbox is for, as the service says: the brand's `system` mailbox (`support@`, created with
 *  the domain to receive the CRM's mail), the `owner`'s free one, or a `user` mailbox after it. */
export type MailboxRole = 'owner' | 'user' | 'system';

/** The slot a mailbox occupies (see {@link MailboxRecord.billing}): TWO values, as the service sends
 *  them. Whether a free mailbox is the brand's (`system`) or the owner's is `role`'s to say. */
export type MailboxBilling = 'free' | 'paid';

/** The service's rule from role to slot (`system` and `owner` free, `user` paid), for an answer that
 *  omits `billing` or sends a value we do not know (an older deployment's third one included) and
 *  for rows stored before the field existed. */
export const billingOfRole = (role: MailboxRole): MailboxBilling => (role === 'user' ? 'paid' : 'free');

/**
 * The DOMAIN's setup/DNS status, as `GET /domains/{domain}` returns it: one per section, not per
 * mailbox. A new domain passes through `relay_pending` (CRM mail's receiving end being set up) and
 * `forward_pending` (the provider confirming the forward) before `dns_pending` (MX published, DNS
 * propagating); the service asks for those three waiting states to render identically. The
 * mailboxes already sign in and send while they last; `failed` carries a `failureReason`. A status
 * the service adds later is read as `pending` by the client, never as an error.
 */
export type DomainStatus =
  | 'pending'
  | 'order_created'
  | 'relay_pending'
  | 'forward_pending'
  | 'dns_pending'
  | 'dns_verified'
  | 'active'
  | 'suspended'
  | 'failed';

/**
 * One mailbox, as `GET /domains/{domain}/accounts` returns it. Identity and status only: the list
 * endpoint returns NO usage (storage, messages, last received), which is a separate per-mailbox call
 * modelled by {@link MailboxUsage}. Keyed on `email` everywhere, as the API is; the local part is a
 * create-form concern and is not stored.
 */
export type MailboxRecord = {
  /** The full address, lowercase. The API is keyed on it, and so is everything in this section. */
  email: string;
  domain: string;
  /** First mailbox on the domain is `owner`, every later one `user`. */
  role: MailboxRole;
  status: MailboxStatus;
  createdAt: string;
  /**
   * When the password was last set or reset (ISO). A DATE, not a secret, so unlike the plaintext
   * it persists with the mailbox. Its presence is what lets the UI say "Password set on 3
   * September" instead of showing nothing on a return visit.
   */
  passwordSetAt?: string;
  /** How many times the password has been rotated since the mailbox was created. */
  passwordRotations: number;
  aliases?: string[];

  /**
   * Which slot this mailbox occupies, from the service (`GET /accounts`), derived there from `role`
   * so the two never disagree: `free` (the brand's system mailbox, `support@`, and the owner's first
   * one; `role` tells them apart) or `paid` (every one after it). It NAMES THE SLOT, NOT A PRICE. It
   * is the whole of a mailbox's billing on screen: a paid mailbox is charged once, at creation, and
   * never again (the contract has no expiry, grace period or later charge). Only a `paid` mailbox
   * can be deleted (the service refuses both free ones with a 409).
   */
  billing: MailboxBilling;

  // ---- ours, not the provider's ----
  /** The invoice this mailbox was bought on. Absent for a free one. */
  invoiceId?: string;
};

/**
 * `GET /domains/{domain}/accounts/{email}/usage`. Read through, never cached: it costs the service
 * two upstream round trips, so it is fetched per mailbox rather than with the list.
 *
 * Every optional field is ABSENT WHEN UNKNOWN, and absent is never zero: a UI that renders a
 * missing `bytesMax` as "of 0" or a missing `unreadMail` as "no unread mail" is stating something
 * the provider never said.
 */
export type MailboxUsage = {
  email: string;
  bytesUsed: number;
  /** ABSENT means the provider reports no ceiling, never zero. */
  bytesMax?: number;
  /** ABSENT where there is no ceiling to subtract from. Nullable in the contract so that a full
   *  mailbox's legitimate zero is never omitted and read as unknown. */
  bytesRemaining?: number | null;
  messagesUsed: number;
  messagesMax?: number;
  /** A boolean, never a count. ABSENT (or `null`, which the contract allows) means not known. */
  unreadMail?: boolean | null;
  /** Only when `?lastReceived=true`. INBOX-only, and it moves BACKWARDS when mail is filed. ABSENT
   *  (or `null`) when there is no message to answer with. */
  lastReceivedAt?: string | null;
  smtpSent?: number;
  /** The MAILBOX's own 24h limit, not the domain's. Commonly 25 on a new mailbox. */
  smtpMax?: number;
};

/** `GET /domains/{domain}`. One per section, not per mailbox: its seed and poll mock live in
 *  `mailbox-domain.ts`. */
export type MailboxDomain = {
  domain: string;
  /** WHERE in the sequence the domain is: what to render while waiting. Never "is mail working": that
   *  is `ready`, and whether the domain is still being set up (the notice, the cards' wait, the poll)
   *  is the section's one rule, `mailboxDomainAwaitingSetup`, which reads `ready` first. */
  status: DomainStatus;
  /** Whether this domain's mail is WORKING (receiving included), from the service. THE field to
   *  branch on for that question, not `status`: the status list is the service's and grows (two new
   *  waiting states broke callers that compared statuses), while `ready` keeps its meaning. */
  ready: boolean;
  mailHost?: string;
  manageDns: boolean;
  failureReason?: string;
};

/**
 * `ready` when the service did not send it: `status === 'active'`, the rule the service states for
 * it today. The FALLBACK EXISTS ONLY for an older deployment that omits the field (and for state
 * stored before it existed); a service that sends `ready` is always believed, whatever its status.
 */
export const mailboxDomainReadyFallback = (status: string): boolean => status === 'active';

/**
 * `POST /domains/{domain}/accounts/{email}/sso`: everything a browser needs to sign in to webmail
 * as the mailbox. NOT A LINK, and it cannot be made into one: the webmail accepts it only as a form
 * POST (the same values as a GET answer 401), and a credential in a URL lands in history, the
 * referrer and every proxy log. The field names travel with the token so the webmail's internals
 * are never hardcoded here.
 *
 * `token` signs in as the mailbox: never store it, never reuse it (one click, one call), never log
 * it. Its mock lives in `mailbox-sso.ts`.
 */
export type MailboxSso = {
  /** The mailbox, and the value for `usernameField`. */
  email: string;
  /** Where the form is posted. */
  loginUrl: string;
  usernameField: string;
  /** The form field the token goes in. */
  passwordField: string;
  token: string;
  /** Optional in the contract (`swagger.yml` does not require it). */
  expiresInHours?: number;
  /** True when the token dies on first redemption. Optional in the contract. */
  singleUse?: boolean;
};

/**
 * `GET /domains/{domain}/mailbox-price`: what ONE paid mailbox costs, charged once, at its creation,
 * from the owner's main wallet. `amount` is in MAJOR units of `currency` (see `toMailboxPrice` in the
 * page's `api/client.ts`, the one place the service's number is interpreted). `0` means charging is
 * switched off in that deployment, not a price of zero to show. The only source of a price: nothing in
 * this app decides what a mailbox costs.
 */
export type MailboxPrice = { amount: number; currency: string };

/** The section's read of the price: not answered yet, answered, or the read failed. Until it is
 *  `ready`, no number is shown anywhere. */
export type MailboxPriceRead = { status: 'loading' } | { status: 'ready'; price: MailboxPrice } | { status: 'failed' };

/** The error codes the service answers with. Stable: branch on these, never on `message`. */
/** `unauthorized` (401: the owner's token is missing or no longer resolves) and `forbidden` (403: it
 *  resolved, but the domain is not this owner's) arrived with the owner-token transport.
 *  `insufficient_credits` (402) is a create the owner's MAIN wallet could not pay for: NOTHING was
 *  created and nothing was charged, so the same create succeeds after a top-up (unlike a `409`, where
 *  retrying never helps). The code still says credits; the money is the main wallet's.
 *  `not_eligible` is a domain that will never have mailboxes: the Settings index hides the Mailbox row
 *  for it (see `useMailboxEligibility`). */
export type MailboxErrorCode =
  | 'invalid_request'
  | 'not_found'
  | 'conflict'
  | 'upstream_failed'
  | 'unauthorized'
  | 'forbidden'
  | 'insufficient_credits'
  | 'not_eligible';

/**
 * Every error the service answers, one shape. `message` is WRITTEN FOR AN OPERATOR: show it, do not
 * replace it with a generic line (it names the rule that was broken, the address that exists, the
 * domain that is unknown). `upstream` is the provider's own words on a `502`: log it, never show it.
 */
export type MailboxError = { code: MailboxErrorCode; message: string; upstream?: string };

const MAILBOX_ERROR_CODES: ReadonlySet<string> = new Set<MailboxErrorCode>([
  'invalid_request',
  'not_found',
  'conflict',
  'upstream_failed',
  'unauthorized',
  'forbidden',
  'insufficient_credits',
  'not_eligible',
]);

/** Whether a rejection is the service's own error (as opposed to, say, a network failure). */
export const isMailboxError = (value: unknown): value is MailboxError => {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.code === 'string' &&
    MAILBOX_ERROR_CODES.has(e.code) &&
    typeof e.message === 'string' &&
    (e.upstream === undefined || typeof e.upstream === 'string')
  );
};

/** The one canonical form of an address: trimmed and lowercase, so a key never differs by case. */
export const mailboxEmail = (name: string, domain: string): string =>
  `${name.trim().toLowerCase()}@${domain.trim().toLowerCase()}`;

/**
 * The mail domain a brand with no domain on record shows (a dev-only walkable section). ONE value:
 * the seed's addresses are built on it, so two fallbacks would resolve two different sets of rows
 * for the same brand.
 */
export const MAILBOX_FALLBACK_DOMAIN = 'yourbrand.com';

/**
 * How many mailboxes are included with the domain and how many exist. There is NO cap: an operator
 * may create as many as they want, paying for each beyond `included`. `used` is authoritative from
 * the server and must never be inferred by counting rows, because the included allowance is a
 * commercial decision that will change.
 */
export type MailboxEntitlement = { included: number; used: number };

/** How many stand-in datasets the brand switcher cycles through. */
export const MAILBOX_VARIANT_COUNT = 7;

/** One gibibyte in bytes, the unit quotas are expressed in. */
const GIB = 1024 ** 3;

/** Default per-mailbox storage ceiling IN BYTES: 5 GiB (5368709120), what `/usage` reports as
 *  `bytesMax` for a 5 GB mailbox. `GIB`, not a decimal billion: the storage formatter divides by
 *  1024^3, so this renders as "5 GB" where 5e9 would read "4.7 GB". Every mailbox is this size; a
 *  size picker is an open product decision (what a size costs is the service's price to answer). */
export const DEFAULT_MAILBOX_QUOTA_BYTES = 5 * GIB;

/**
 * The same default IN MEGABYTES, the value sent as `quotaMb` when a mailbox is created.
 *
 * UNIT TRAP: `quotaMb` is MEGABYTES (MiB), even though OpenSRS's own docs call the field bytes.
 * The settable range is [0, 20480], so sending the byte figure (5368709120) is rejected outright.
 * 5 GiB = 5120 MiB, and 5120 is also ONE billing increment (OpenSRS bills in 5 GB steps), so any
 * future size must be a multiple of 5120.
 */
export const DEFAULT_MAILBOX_QUOTA_MB = 5120;

/**
 * Simulated latencies standing in for the provider and for DNS. Every one of these disappears when
 * the real endpoints land.
 */
export const MAILBOX_SIMULATED = {
  /** Submit (or Pay) -> the create round trip; the row then appears already `active`. */
  createMs: 700,
  /** Password reset round trip. */
  resetMs: 600,
  /** A payment settling. */
  settleMs: 900,
} as const;

/** ISO timestamp `days` days before now, for `createdAt` / `passwordSetAt` labels. */
export const daysAgo = (days: number): string => new Date(Date.now() - days * 86_400_000).toISOString();

/** ISO timestamp `hours` hours before now, for recent `lastReceivedAt` labels. */
export const hoursAgo = (hours: number): string => new Date(Date.now() - hours * 3_600_000).toISOString();

/**
 * The brand's SYSTEM mailbox, `support@`: the service creates it with the domain's first
 * `owner-mailbox` call (role `system`, billing `free`, never deletable) and lists it from then on, so
 * every variant that has mailboxes lists it first, as `GET /accounts` does. Nobody knows its password
 * (it is generated and never returned), hence no `passwordSetAt`.
 */
const systemMailbox = (domain: string): MailboxRecord => ({
  email: mailboxEmail('support', domain),
  domain,
  role: 'system',
  status: 'active',
  passwordRotations: 0,
  billing: 'free',
  createdAt: daysAgo(40),
});

/** The owner's free mailbox every populated variant has: the first an operator created, so its
 *  `owner`. */
const includedMailbox = (domain: string, over: Partial<MailboxRecord> = {}): MailboxRecord => ({
  email: mailboxEmail('hello', domain),
  domain,
  role: 'owner',
  status: 'active',
  passwordSetAt: daysAgo(12),
  passwordRotations: 1,
  billing: 'free',
  createdAt: daysAgo(40),
  ...over,
});

/**
 * The seed mailboxes for a brand at cycle position `variant`, on `domain` (the brand's mail domain:
 * the seed cannot build an address without it). Usage is NOT here, because the list endpoint does
 * not return it: the same variants' usage lives in `mailbox-usage.ts`.
 * Every populated variant lists the brand's `support@` (role `system`) first, as the service does.
 *  - 0 -> empty: no mailboxes yet (support@ comes with the first create), so the first-run grid
 *         (both add-tiles) is reachable.
 *  - 1 -> one free `active` mailbox on low storage: the common healthy case.
 *  - 2 -> the free one plus a paid `active` mailbox, near quota (past the
 *         storage warning ratio), so the warning treatment is reachable.
 *  - 3 -> the free one plus a paid `active` mailbox with an alias and real traffic.
 *  - 4 -> the free one, a paid mailbox the provider has `suspended`, and one it reports `deleted`,
 *         so both provider statuses render from seed data.
 *  - 5 -> one free mailbox on a domain whose setup FAILED (see `mailbox-domain.ts`), so the
 *         section's error notice and its "no mailbox actions" treatment render from seed data.
 *  - 6 -> none, on a domain the service has NEVER HEARD OF (its list answers `404`, not `[]`; see
 *         `mailbox-domain.ts`), so the "no mail service yet" empty state renders from seed data and
 *         differs from variant 0's "registered, no mailboxes".
 *
 * The DOMAIN's status per variant (variant 2 is the one still waiting on DNS) is in
 * `mailbox-domain.ts`.
 */
export const buildMailboxes = (variant: number, rawDomain: string): MailboxRecord[] => {
  const v = ((variant % MAILBOX_VARIANT_COUNT) + MAILBOX_VARIANT_COUNT) % MAILBOX_VARIANT_COUNT;
  // Canonical like every address built on it, so a row's `domain` and `email` always agree.
  const domain = rawDomain.trim().toLowerCase();
  switch (v) {
    case 1:
      return [systemMailbox(domain), includedMailbox(domain)];
    case 2:
      return [
        systemMailbox(domain),
        includedMailbox(domain, { passwordSetAt: daysAgo(30) }),
        {
          // Near full on purpose (see its usage): past the storage warning ratio.
          email: mailboxEmail('archive', domain),
          domain,
          role: 'user',
          status: 'active',
          passwordSetAt: daysAgo(70),
          passwordRotations: 0,
          billing: 'paid',
          invoiceId: 'inv-seed-archive',
          createdAt: daysAgo(80),
        },
      ];
    case 3:
      return [
        systemMailbox(domain),
        includedMailbox(domain, { passwordSetAt: daysAgo(20) }),
        {
          // A paid mailbox in daily use, with an alias. Not support@: that address is the system
          // mailbox's on every domain.
          email: mailboxEmail('team', domain),
          domain,
          role: 'user',
          status: 'active',
          passwordSetAt: daysAgo(9),
          passwordRotations: 3,
          aliases: [mailboxEmail('help', domain)],
          billing: 'paid',
          invoiceId: 'inv-seed-support',
          createdAt: daysAgo(360),
        },
      ];
    case 4:
      return [
        systemMailbox(domain),
        includedMailbox(domain, { passwordSetAt: daysAgo(50) }),
        {
          // Suspended at the provider: listed with its pill, nothing to press (the service offers no
          // resume).
          email: mailboxEmail('sales', domain),
          domain,
          role: 'user',
          status: 'suspended',
          passwordSetAt: daysAgo(45),
          passwordRotations: 0,
          billing: 'paid',
          invoiceId: 'inv-seed-sales',
          createdAt: daysAgo(70),
        },
        {
          // Deleted at the provider but still returned by the list endpoint: rendered with a pill,
          // no primary action and no webmail, and never counted as a live mailbox. (This slot used
          // to demo a per-mailbox `failed`; failure is a DOMAIN status now, demoed from §4.)
          email: mailboxEmail('newsletter', domain),
          domain,
          role: 'user',
          status: 'deleted',
          passwordRotations: 0,
          billing: 'paid',
          createdAt: daysAgo(1),
        },
      ];
    case 5:
      return [systemMailbox(domain), includedMailbox(domain)];
    case 0:
    default:
      return [];
  }
};

/**
 * The entitlement for a brand at cycle position `variant`. `included` is the free allowance (1
 * today); `used` is authoritative from the server and matches {@link buildMailboxes}'s count of the
 * OPERATOR's live mailboxes for the same variant (the rule the store recomputes it by), so the seed
 * is internally consistent. A `deleted` row is not a seat, and neither is support@ (role `system`):
 * the brand's, not the operator's. The count does not depend on the domain, so any domain builds it.
 */
export const buildMailboxEntitlement = (variant: number): MailboxEntitlement => {
  const v = ((variant % MAILBOX_VARIANT_COUNT) + MAILBOX_VARIANT_COUNT) % MAILBOX_VARIANT_COUNT;
  return {
    included: 1,
    used: buildMailboxes(v, MAILBOX_FALLBACK_DOMAIN).filter((m) => m.status !== 'deleted' && m.role !== 'system').length,
  };
};

// --- The persisted per-brand record, and how a brand's rows are resolved ------------------------
//
// Owned HERE, beside the seed, and not in the page folder: the section resolves a brand's rows as
// "the operator's saved set if there is one, else the seed variant for that brand". The page store
// imports the read and the resolver from here (the same inversion as the wallet ledger and the
// storage key), so with the page folder deleted this file still compiles on its own and the key
// simply stops being written.

/**
 * The per-brand record the section persists: `Record<brandId, { mailboxes, entitlement }>`.
 * The value was changed with the rename and the previous key is deliberately left orphaned, not
 * reused: if anyone restores the pre-rename folder from its archive tag, two code paths writing one
 * key would be a hard-to-see corruption. Demo state resets as a result, which is expected.
 */
export const MAILBOX_STATE_KEY = 'ows_mailbox_state_v1';

// A saved record from before the status narrowing carries `state` (with members that no longer
// exist) and no `status`, so it fails this guard and the brand falls back to its seed: demo state
// resets once, by the validator's own fail-safe, rather than by churning the key again.
const VALID_STATUSES: ReadonlySet<MailboxStatus> = new Set<MailboxStatus>(['active', 'suspended', 'deleted']);

const isFiniteNonNegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

const VALID_ROLES: ReadonlySet<MailboxRole> = new Set<MailboxRole>(['owner', 'user', 'system']);
const VALID_BILLINGS: ReadonlySet<MailboxBilling> = new Set<MailboxBilling>(['free', 'paid']);

/** Runtime guard: localStorage is untrusted, so validate each record's shape before trusting it. A
 *  record from before the billing cadence was cut may still carry a `term` key; it is ignored (the
 *  expiry lifecycle's keys never reach here: `dropExpiryLifecycle` removed them on read). A
 *  record from before the list/usage split carries `id` and `localPart` and no `email`, so it fails
 *  here and the brand falls back to its seed once (the same fail-safe as the status narrowing). */
const isMailbox = (value: unknown): value is MailboxRecord => {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.email === 'string' &&
    m.email.includes('@') &&
    typeof m.domain === 'string' &&
    typeof m.role === 'string' &&
    VALID_ROLES.has(m.role as MailboxRole) &&
    typeof m.status === 'string' &&
    VALID_STATUSES.has(m.status as MailboxStatus) &&
    isFiniteNonNegative(m.passwordRotations) &&
    (m.aliases === undefined || (Array.isArray(m.aliases) && m.aliases.every((a) => typeof a === 'string'))) &&
    typeof m.billing === 'string' &&
    VALID_BILLINGS.has(m.billing as MailboxBilling) &&
    typeof m.createdAt === 'string'
  );
};

const isMailboxList = (value: unknown): value is MailboxRecord[] => Array.isArray(value) && value.every(isMailbox);

const isEntitlement = (value: unknown): value is MailboxEntitlement => {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Record<string, unknown>;
  return isFiniteNonNegative(e.included) && isFiniteNonNegative(e.used);
};

const VALID_DOMAIN_STATUSES: ReadonlySet<DomainStatus> = new Set<DomainStatus>([
  'pending',
  'order_created',
  'relay_pending',
  'forward_pending',
  'dns_pending',
  'dns_verified',
  'active',
  'suspended',
  'failed',
]);

const isMailboxDomain = (value: unknown): value is MailboxDomain => {
  if (typeof value !== 'object' || value === null) return false;
  const d = value as Record<string, unknown>;
  return (
    typeof d.domain === 'string' &&
    typeof d.status === 'string' &&
    VALID_DOMAIN_STATUSES.has(d.status as DomainStatus) &&
    typeof d.ready === 'boolean' &&
    typeof d.manageDns === 'boolean' &&
    (d.mailHost === undefined || typeof d.mailHost === 'string') &&
    (d.failureReason === undefined || typeof d.failureReason === 'string')
  );
};

/**
 * One brand's persisted record: mailboxes + entitlement, written together, and the domain once the
 * section has changed it (a finished DNS poll, the walk back after the last delete). `mailDomain`
 * is optional so a record saved before the domain existed still validates; the brand's seed domain
 * fills in for it. `null` is a domain the service has never heard of (`404`).
 */
export type MailboxBrandRecord = {
  mailboxes: MailboxRecord[];
  entitlement: MailboxEntitlement;
  mailDomain?: MailboxDomain | null;
};

const isBrandRecord = (value: unknown): value is MailboxBrandRecord => {
  if (typeof value !== 'object' || value === null) return false;
  const r = value as Record<string, unknown>;
  return (
    isMailboxList(r.mailboxes) &&
    isEntitlement(r.entitlement) &&
    (r.mailDomain === undefined || r.mailDomain === null || isMailboxDomain(r.mailDomain))
  );
};

export type MailboxPersistedMap = Record<string, MailboxBrandRecord>;

/** A row stored before `billing` existed carried `billed: boolean` instead: read it once as a slot
 *  (`paid` when billed, else `free`), so an operator's saved state survives the change. */
const upgradeBilledRow = (row: unknown): unknown => {
  if (typeof row !== 'object' || row === null) return row;
  const { billed, ...rest } = row as Record<string, unknown>;
  if (rest.billing !== undefined || typeof billed !== 'boolean') return row;
  return { ...rest, billing: billed ? 'paid' : 'free' };
};

/** A row stored while the section modelled an expiry lifecycle (renewal, expiring, disabled) carried
 *  `expiry`, `daysUntilExpiry` and `graceDaysLeft`. The contract has none of it (a paid mailbox is
 *  charged once, at creation), so they are dropped on read and never written back. */
const dropExpiryLifecycle = (row: unknown): unknown => {
  if (typeof row !== 'object' || row === null) return row;
  const { expiry, daysUntilExpiry, graceDaysLeft, ...rest } = row as Record<string, unknown>;
  void expiry;
  void daysUntilExpiry;
  void graceDaysLeft;
  return rest;
};

const upgradeStoredRow = (row: unknown): unknown => dropExpiryLifecycle(upgradeBilledRow(row));

/** A domain stored before `ready` existed: derived from its status, by the same fallback as an
 *  older deployment's answer. */
const upgradeStoredDomain = (domain: unknown): unknown => {
  if (typeof domain !== 'object' || domain === null) return domain;
  const d = domain as Record<string, unknown>;
  if (d.ready !== undefined || typeof d.status !== 'string') return domain;
  return { ...d, ready: mailboxDomainReadyFallback(d.status) };
};

const upgradeStoredRecord = (value: unknown): unknown => {
  if (typeof value !== 'object' || value === null) return value;
  const r = value as Record<string, unknown>;
  const upgraded = Array.isArray(r.mailboxes) ? { ...r, mailboxes: r.mailboxes.map(upgradeStoredRow) } : r;
  return 'mailDomain' in upgraded ? { ...upgraded, mailDomain: upgradeStoredDomain(upgraded.mailDomain) } : upgraded;
};

/** Read the whole per-brand map, dropping any brand whose stored value fails validation. `{}` on a
 *  missing key, malformed JSON, or blocked storage. */
export const readMailboxBrandRecords = (): MailboxPersistedMap => {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(MAILBOX_STATE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: MailboxPersistedMap = {};
    for (const [brandId, value] of Object.entries(parsed as Record<string, unknown>)) {
      const record = upgradeStoredRecord(value);
      if (isBrandRecord(record)) out[brandId] = record;
    }
    return out;
  } catch {
    return {};
  }
};

/** The operator's saved record for a brand, or `null` when they have never changed it. */
export const loadMailboxBrandRecord = (brandId: string): MailboxBrandRecord | null => {
  const all = readMailboxBrandRecords();
  return Object.prototype.hasOwnProperty.call(all, brandId) ? all[brandId] : null;
};

/**
 * Which seed variant a brand shows: its position in the owner's brand list, cycling, so switching
 * brands walks every state. ONE formula, so no two readers resolve different seeds for the same
 * brand. -1 (brand not in the list) resolves to variant 0.
 */
export const mailboxSeedVariantFor = (brands: ReadonlyArray<{ id: string }>, brandId: string | undefined): number => {
  if (!brandId) return 0;
  const idx = brands.findIndex((brand) => brand.id === brandId);
  return ((Math.max(0, idx) % MAILBOX_VARIANT_COUNT) + MAILBOX_VARIANT_COUNT) % MAILBOX_VARIANT_COUNT;
};

/**
 * The mailboxes a brand starts from: the operator's saved set if they have changed anything,
 * otherwise the mock seed (unchanged reference, so untouched brands keep their live seed).
 */
export const resolveInitialMailboxes = (brandId: string | undefined, seed: MailboxRecord[]): MailboxRecord[] =>
  (brandId ? loadMailboxBrandRecord(brandId)?.mailboxes : null) ?? seed;
